import { UniversalToolCall, OpenAIToolCall, GeminiToolCall, ClaudeToolCall } from '@app-libs/model';

/**
 * Generate a unique ID for tools that don't have one
 */
function generateToolId(): string {
  return `tool_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Transform provider-specific tool formats to UniversalToolCall
 * 
 * @param tool - The tool call in provider-specific format
 * @param provider - The provider name ('openai', 'gemini', 'claude')
 * @returns Normalized UniversalToolCall format
 */
export function normalizeToolCall(
  tool: any,
  provider?: string
): UniversalToolCall {
  // If already in universal format, return as-is
  if (isUniversalToolCall(tool)) {
    return tool;
  }

  switch (provider?.toLowerCase()) {
    case 'openai':
      return normalizeOpenAITool(tool as OpenAIToolCall);
    
    case 'gemini':
      return normalizeGeminiTool(tool as GeminiToolCall);
    
    case 'claude':
      return normalizeClaudeTool(tool as ClaudeToolCall);
      
    default:
      // Attempt to extract from common patterns
      return {
        id: tool.id || generateToolId(),
        name: extractToolName(tool),
        arguments: extractToolArguments(tool),
        provider_metadata: { original_format: tool }
      };
  }
}

/**
 * Check if the tool is already in UniversalToolCall format
 */
function isUniversalToolCall(tool: any): tool is UniversalToolCall {
  return (
    typeof tool === 'object' &&
    'id' in tool &&
    'name' in tool &&
    'arguments' in tool &&
    typeof tool.name === 'string'
  );
}

/**
 * Normalize OpenAI tool format
 */
function normalizeOpenAITool(tool: OpenAIToolCall): UniversalToolCall {
  return {
    id: tool.id,
    name: tool.function?.name || '',
    arguments: parseArguments(tool.function?.arguments),
    provider_metadata: { provider: 'openai', type: tool.type }
  };
}

/**
 * Normalize Gemini tool format
 */
function normalizeGeminiTool(tool: GeminiToolCall): UniversalToolCall {
  return {
    id: generateToolId(), // Gemini doesn't provide IDs
    name: tool.function_call?.name || '',
    arguments: tool.function_call?.args || {},
    provider_metadata: { provider: 'gemini' }
  };
}

/**
 * Normalize Claude tool format
 */
function normalizeClaudeTool(tool: ClaudeToolCall): UniversalToolCall {
  return {
    id: tool.id,
    name: tool.name,
    arguments: tool.input || {},
    provider_metadata: { provider: 'claude', type: tool.type }
  };
}

/**
 * Extract tool name from various formats
 */
function extractToolName(tool: any): string {
  // Try various common patterns
  return (
    tool.name ||
    tool.function?.name ||
    tool.function_call?.name ||
    tool.tool_name ||
    ''
  );
}

/**
 * Extract tool arguments from various formats
 */
function extractToolArguments(tool: any): Record<string, any> {
  // Try to extract from various locations
  const args = 
    tool.arguments ||
    tool.function?.arguments ||
    tool.function_call?.args ||
    tool.input ||
    tool.parameters ||
    {};
  
  return parseArguments(args);
}

/**
 * Parse arguments that might be JSON strings
 */
function parseArguments(args: any): Record<string, any> {
  if (typeof args === 'string') {
    try {
      return JSON.parse(args);
    } catch {
      return { raw: args };
    }
  }
  return args || {};
}

/**
 * Transform an array of tool calls to normalized format
 */
export function normalizeToolCalls(
  tools: any[],
  provider?: string
): UniversalToolCall[] {
  if (!Array.isArray(tools)) {
    return [];
  }
  
  return tools.map(tool => normalizeToolCall(tool, provider));
}

/**
 * Detect provider from tool structure (best effort)
 */
export function detectProviderFromTool(tool: any): string | undefined {
  if (!tool || typeof tool !== 'object') {
    return undefined;
  }

  // OpenAI pattern: has type: 'function' and function.name
  if (tool.type === 'function' && tool.function?.name) {
    return 'openai';
  }

  // Gemini pattern: has function_call
  if (tool.function_call?.name) {
    return 'gemini';
  }

  // Claude pattern: has type: 'tool_use' and direct name
  if (tool.type === 'tool_use' && tool.name && tool.input) {
    return 'claude';
  }

  return undefined;
}